import { getEmailFeaturedJobPublic } from "@punchup/data/server";
import { SupabaseClient } from "@supabase/supabase-js";
import type { VenuePageTimezones } from "@punchup/data/models/venue_pages";
import { TixologiResumedEvent } from "@punchup/data/legacy/tixologi";
import dayjs from "@punchup/utils/dayjs";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { bodyHtmlToPlainTextClient } from "@punchup/utils/rich-text/plain-text-to-body-html";
import {
  ShowGroupLineupMember,
  toLineupMembers,
} from "../client/components/show-groups/utils";
import {
  type ShowWithComedian,
  getShowsByIds,
} from "@punchup/data/legacy/shows";
import { getTixologiEvents } from "@punchup/data/legacy/hooks";
import { getVenuePage, resolveVenueTimezone } from "../data/venue_pages";
import { VenueShowGroupData } from "./getVenueShowGroupData";
import {
  buildEventLineups,
  buildTixologiAvailabilityMaps,
  dedupeEventsByNameAndDate,
  expandEventDays,
  filterAndSortUpcomingEvents,
} from "./showGroupEvents";
import { logException } from "@punchup/sentry";

export type GetVenueEmailFeaturedShowsDataResult =
  | { success: true; data: VenueShowGroupData }
  | { success: false; notFound: true };

/**
 * Synthesize a Tixologi-event-shaped object for an externally-ticketed show
 * (null `tixologi_event_id`) so it can flow through the shared show-group
 * rendering pipeline. Only the fields the page and its helpers actually read
 * are populated (`id`, `name`, `start_date`, `end_date`, `time_zone`,
 * `image`, `images`, `description`, `street_address`, `ticket_types`,
 * `days`); the rest of the wide Tixologi interface is absent behind the
 * cast. The id is a NEGATIVE sequential number — guaranteed disjoint from
 * real (positive) Tixologi ids, so every `String(ev.id)`-keyed map stays
 * collision-free.
 *
 * `start_date` must be an instant like Tixologi's, but `comedian_shows.datetime`
 * is the venue's wall clock with no offset (see `showDayWindow`) — so it's read
 * in the show's own timezone before it joins the pipeline, else every external
 * show renders shifted by the venue's UTC offset and sorts/cuts off wrongly
 * against the real events.
 */
function synthesizeEventFromShow(
  show: ShowWithComedian,
  syntheticId: number,
  timezone: string
): TixologiResumedEvent {
  const startInstant = show.datetime
    ? dayjs.tz(show.datetime, timezone).toISOString()
    : "";
  const synthesized: Partial<TixologiResumedEvent> = {
    id: syntheticId,
    name: show.title ?? "",
    start_date: startInstant,
    end_date: startInstant,
    time_zone: timezone,
    image: show.poster_img ? getCDNImageURL(show.poster_img) : "",
    images: [],
    // comedian_shows has no description column; metadata_text is the show's
    // free-text blurb (ShowInfo.metadataText). It may now be rich HTML
    // (PUN-5353); the email renders it as plain text, so flatten it here. The
    // flattener seeds first, so a legacy row keeps its paragraph breaks and a
    // literal `<VIP>`. It is the cheerio-free one because the client hooks
    // barrel reaches this module through venuePageCache.
    description: bodyHtmlToPlainTextClient(show.metadata_text ?? ""),
    street_address: show.location ?? "",
    ticket_types: [],
    days: [],
  };
  return synthesized as TixologiResumedEvent;
}

/**
 * Page data for the email featured-shows landing page
 * (`/shows/featured/[jobId]`) — the CTA target of a venue email featuring 2+
 * shows. Renders LIVE from the job's `focusShowIds` (no snapshot, no
 * `show_group_link` row), so it never surfaces in the venue's grouped grid,
 * sitemap, or Generate & Share Links dashboard, and a manager can't break a
 * sent email by deleting anything. Unlike the show-group page it supports
 * externally-ticketed shows: they're synthesized into the same event shape
 * with their ticket URL carried in `externalTicketLinks`.
 *
 * Degrades gracefully rather than 404ing: shows that were deleted, unlinked
 * from the venue, or are past render as "not there" — the page shows the
 * remaining ones, or its empty state when none survive. 404 is reserved for
 * an unknown venue/job or a job that doesn't belong to this venue.
 */
export async function getVenueEmailFeaturedShowsData(
  supabase: SupabaseClient,
  venueSlug: string,
  jobId: string
): Promise<GetVenueEmailFeaturedShowsDataResult> {
  const venue = await getVenuePage(supabase, { slug: venueSlug }).catch(
    () => null
  );
  if (!venue) return { success: false, notFound: true };

  const job = await getEmailFeaturedJobPublic({ jobId }).catch((error) => {
    logException(error instanceof Error ? error : new Error(String(error)), {
      tags: { source: "getVenueEmailFeaturedShowsData" },
      extra: { venueSlug, jobId },
    });
    return null;
  });
  if (!job || job.venuePageId !== venue.id) {
    return { success: false, notFound: true };
  }

  const showsById = await getShowsByIds(job.focusShowIds).catch((error) => {
    logException(error instanceof Error ? error : new Error(String(error)), {
      tags: { source: "getVenueEmailFeaturedShowsData:shows" },
      extra: { venueSlug, jobId },
    });
    return new Map<string, ShowWithComedian>();
  });
  const venueShows = Array.from(showsById.values()).filter((show) =>
    show.venue_pages?.some((vp) => vp.id === venue.id)
  );

  const timezones = (venue.timezones ?? []) as VenuePageTimezones;
  const venueTimezone = resolveVenueTimezone(timezones, null);

  const tixologiShows = venueShows.filter((s) => s.tixologi_event_id);
  const externalShows = venueShows.filter((s) => !s.tixologi_event_id);

  // Tixologi-backed half: fetch the real events, then shape them exactly like
  // the show-group page does (day-expansion + same-instance dedupe). The
  // dedupe runs on this half only so a same-named external show can never be
  // collapsed into a Tixologi record.
  let tixologiEvents: TixologiResumedEvent[] = [];
  const tixologiEventIds = [
    ...new Set(tixologiShows.map((s) => String(s.tixologi_event_id))),
  ];
  if (tixologiEventIds.length > 0) {
    try {
      const response = await getTixologiEvents({
        ids: tixologiEventIds,
        date: "live_or_scheduled",
        perPage: -1,
      });
      tixologiEvents = Array.isArray(response) ? response : [];
    } catch (error) {
      logException(error instanceof Error ? error : new Error(String(error)), {
        tags: { source: "getVenueEmailFeaturedShowsData:tixologi" },
        extra: { venueSlug, jobId },
      });
    }
  }
  tixologiEvents = dedupeEventsByNameAndDate(expandEventDays(tixologiEvents));

  const syntheticEvents = externalShows.map((show, index) =>
    synthesizeEventFromShow(
      show,
      -(index + 1),
      resolveVenueTimezone(timezones, show.venue_id)
    )
  );

  const events = filterAndSortUpcomingEvents(
    [...tixologiEvents, ...syntheticEvents],
    timezones
  );

  // Event key → punchup show id. Tixologi events map through the show's own
  // tixologi_event_id (the shows are already in hand — no extra lookup);
  // synthetics map directly to the show they were synthesized from.
  const eventPageIds: Record<string, string> = {};
  for (const show of tixologiShows) {
    if (show.tixologi_event_id) eventPageIds[show.tixologi_event_id] = show.id;
  }
  const externalTicketLinks: Record<string, string | null> = {};
  externalShows.forEach((show, index) => {
    const key = String(-(index + 1));
    eventPageIds[key] = show.id;
    externalTicketLinks[key] = show.ticket_link ?? null;
  });

  const lineupByShowId = new Map<string, ShowGroupLineupMember[]>();
  for (const show of venueShows) {
    lineupByShowId.set(show.id, toLineupMembers(show.show_comedians));
  }
  const { eventLineups, sharedLineup } = buildEventLineups(
    events,
    eventPageIds,
    lineupByShowId
  );

  // Tixologi-backed availability comes from the same live overlay the venue
  // grid uses; external shows have no live source, so their manual
  // `is_sold_out` flag is authoritative (same as the grid).
  const { soldOutByEventId, waitlistShows } =
    await buildTixologiAvailabilityMaps(tixologiShows);
  externalShows.forEach((show, index) => {
    const key = String(-(index + 1));
    soldOutByEventId[key] = !!show.is_sold_out;
    if (show.is_sold_out && show.venue_id) {
      waitlistShows[key] = show;
    }
  });

  const first = events[0];
  const last = events[events.length - 1];

  return {
    success: true,
    data: {
      series: {
        id: `email-${jobId}`,
        venue_page_id: venue.id,
        title: job.emailSubject.trim() || "Featured Events",
        slug: `featured-${jobId}`,
        // 'weekend' gives the page its date-range subtitle; the hero badge is
        // overridden to "Featured Events" by the route mounts.
        link_type: "weekend",
        event_ids: null,
        start_date: first
          ? dayjs(first.start_date)
              .tz(first.time_zone || venueTimezone)
              .format("YYYY-MM-DD")
          : null,
        end_date: last
          ? dayjs(last.start_date)
              .tz(last.time_zone || venueTimezone)
              .format("YYYY-MM-DD")
          : null,
        day_of_week: null,
        show_time: null,
        show_name: null,
        facebook_pixel_id: null,
        created_at: dayjs(0).toISOString(),
        updated_at: dayjs(0).toISOString(),
      },
      events,
      venueName: venue.name,
      venueAddress: events[0]?.street_address ?? null,
      venueTimezone,
      eventPageIds,
      eventLineups,
      sharedLineup,
      waitlistShows,
      soldOutByEventId,
      externalTicketLinks,
    },
  };
}
